import React, { useRef, useEffect, useMemo } from "react";
import { cn } from "@/utils/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";
import { TriangleArrowRight } from "@dreamhub/designsystem/icons";
import Text from "@/components/ui/text";
import IconContainer from "@/components/ui/icon-container";
import { DataTableFilter } from "@/components/controls/DataTableFilters/DataTableFilters";
import { Filters, FiltersSetter } from "@/services/api/types";
import FilterContent from "./FilterContent";
import FilterMenuItem from "./FilterMenuItem";
import { extractFilterValues } from "@/hooks/useFilterOptions";

interface Props {
  filterOption: DataTableFilter;
  filterLabel: string;
  hasOptions: boolean;
  isSubmenuOpen: boolean;
  onSubmenuOpen: () => void;
  onSubmenuClose: () => void;
  onCloseAll: () => void;
  filters: Filters;
  setFilters: FiltersSetter;
}

const FilterListItem: React.FC<Props> = ({
  filterOption,
  filterLabel,
  hasOptions,
  isSubmenuOpen,
  onSubmenuOpen,
  onSubmenuClose,
  onCloseAll,
  filters,
  setFilters
}) => {
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const isDateFilter = filterOption.filterType.type === "date";
  const isNumericFilter = filterOption.filterType.type === "numeric";
  const isCustomFieldFilter = filterOption.filterType.type === "customField";
  const isBooleanFilter = filterOption.filterType.type === "boolean";
  const hasSubmenu =
    hasOptions ||
    isDateFilter ||
    isNumericFilter ||
    isCustomFieldFilter ||
    isBooleanFilter;

  const activeFilterCount = useMemo(() => {
    const filterKey = filterOption.key;
    const filterValue = filters[filterKey as keyof Filters];
    if (!filterValue) return 0;
    const values = extractFilterValues(filterValue);
    return values.length;
  }, [filterOption.key, filters]);

  const handleMouseEnter = () => {
    if (hasSubmenu) {
      hoverTimeoutRef.current = setTimeout(() => {
        onSubmenuOpen();
      }, 100);
    }
  };

  const handleMouseLeave = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
  };

  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  if (hasSubmenu) {
    return (
      <Popover
        open={isSubmenuOpen}
        onOpenChange={(open) => !open && onSubmenuClose()}
      >
        <PopoverTrigger asChild>
          <FilterMenuItem
            isActive={isSubmenuOpen}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <div className="flex items-center w-full justify-between">
              <Text variant="body">{filterLabel}</Text>
              {activeFilterCount > 0 && !isSubmenuOpen ? (
                <Text variant="bodyTertiary">
                  ({activeFilterCount})
                </Text>
              ) : (
                <IconContainer
                  className={cn(
                    "transition-opacity duration-300 ease-in-out",
                    isSubmenuOpen ? "opacity-100" : "opacity-0"
                  )}
                >
                  <TriangleArrowRight />
                </IconContainer>
              )}
            </div>
          </FilterMenuItem>
        </PopoverTrigger>
        <PopoverContent
          side="right"
          padding={4}
          className={cn("gap-1", isDateFilter && "w-fit")}
          align="start"
          sideOffset={4}
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <FilterContent
            filterOption={filterOption}
            filters={filters}
            setFilters={setFilters}
            onClose={onCloseAll}
            filterLabel={filterLabel}
          />
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <FilterMenuItem variant="strong">
      <div className="flex items-center gap-2">
        <Text>{filterLabel}</Text>
        {activeFilterCount > 0 && (
          <Text variant="bodyTertiary">({activeFilterCount})</Text>
        )}
      </div>
    </FilterMenuItem>
  );
};

export default FilterListItem;
